// Dashboard Configuration
// Provides help text and descriptions for dashboard widgets and metrics

export const dashboardConfig = {
  // Help text for dashboard widgets and metrics
  helpText: {
    // Key Metrics Overview
    metricsOverview: {
      title: 'Key Metrics Overview',
      description: 'Real-time summary of critical QMS metrics across all modules. Shows current status, trends, and performance indicators for quick decision-making.'
    },
    
    // Document Management Metrics
    activeDocuments: { short: 'Documents currently active and in use across the organization.' },
    pendingReviews: { short: 'Documents awaiting review or approval in the workflow.' },
    expiringSoon: { short: 'Documents that may need review soon (older than 10 months).' },
    complianceRate: { short: 'Percentage of documents that are currently compliant and active.' },
    
    // CAPA Management Metrics
    openCapas: { short: 'Corrective and Preventive Actions currently open or in progress.' },
    closedCapas: { short: 'CAPAs that have been completed and closed.' },
    overdueCapas: { short: 'CAPAs that have passed their due date and need attention.' },
    
    // Risk Assessment Metrics
    highRisks: { short: 'Risk assessments with High or Critical risk levels requiring immediate attention.' },
    activeRisks: { short: 'Risk assessments that are currently being monitored or managed.' },
    mitigatedRisks: { short: 'Risks that have been successfully mitigated or reduced.' },
    
    // Non-Conformity Report Metrics
    openNCRs: { short: 'Non-Conformity Reports that are open and being investigated.' },
    closedNCRs: { short: 'NCRs that have been resolved and closed.' },
    criticalNCRs: { short: 'NCRs with critical severity requiring urgent attention.' },
    
    // Change Request Metrics
    pendingChanges: { short: 'Change requests awaiting approval or implementation.' },
    implementedChanges: { short: 'Change requests that have been successfully implemented.' },
    rejectedChanges: { short: 'Change requests that were rejected or cancelled.' },
    
    // Complaint Management Metrics
    openComplaints: { short: 'Customer complaints that are open and being investigated.' },
    resolvedComplaints: { short: 'Complaints that have been resolved and closed.' },
    highPriorityComplaints: { short: 'Complaints with high priority requiring immediate attention.' },
    
    // Training Management Metrics
    pendingTraining: { short: 'Training assignments that are pending or not yet started.' },
    completedTraining: { short: 'Training assignments that have been completed.' },
    overdueTraining: { short: 'Training assignments that are past their due date.' },
    
    // Todo Management Metrics
    openTodos: { short: 'Todo items that are open and need to be completed.' },
    completedTodos: { short: 'Todo items that have been completed.' },
    
    // Widget Descriptions
    documentStatusChart: {
      title: 'Document Status Distribution',
      description: 'Visual breakdown of document statuses showing the distribution of active, review, and other document states.'
    },
    
    complianceTracker: {
      title: 'Compliance Tracker',
      description: 'Tracks compliance metrics and identifies areas that may need attention for regulatory requirements.'
    },
    
    qualityTrends: {
      title: 'Quality Trends',
      description: 'Shows trends in quality metrics over time to identify patterns and improvement opportunities.'
    },
    
    recentActivity: {
      title: 'Recent Activity',
      description: 'Latest activities and changes across all QMS modules for real-time monitoring.'
    },
    
    actionItems: {
      title: 'Action Items',
      description: 'Outstanding action items and tasks that require attention or follow-up.'
    },
    
    capaStatusOverview: {
      title: 'CAPA Status Overview',
      description: 'Overview of Corrective and Preventive Action statuses and progress tracking.'
    },
    
    trainingProgress: {
      title: 'Training Progress',
      description: 'Training completion rates and progress tracking for compliance and skill development.'
    },
    
    supplierOverview: {
      title: 'Supplier Overview',
      description: 'Supplier performance metrics, ratings, and status overview for vendor management.'
    },
    
    complaintOverview: {
      title: 'Complaint Overview',
      description: 'Customer complaint metrics, resolution rates, and status tracking.'
    },
    
    ncrOverview: {
      title: 'Non-Conformity Report Overview',
      description: 'NCR metrics, severity distribution, and resolution tracking.'
    },
    
    todoManagement: {
      title: 'Todo Management',
      description: 'Task management overview showing open and completed todos across the system.'
    },
    
    riskMatrix: {
      title: 'Risk Assessment Matrix',
      description: 'Visual risk matrix showing risk levels, categories, and mitigation status.'
    }
  },

  // Widget configuration
  widgets: {
    metricsOverview: {
      title: 'Key Metrics',
      description: 'Real-time overview of critical QMS metrics',
      refreshInterval: 300000, // 5 minutes
      priority: 1
    },
    documentStatusChart: {
      title: 'Document Status',
      description: 'Document status distribution chart',
      refreshInterval: 300000,
      priority: 2
    },
    complianceTracker: {
      title: 'Compliance',
      description: 'Compliance tracking and metrics',
      refreshInterval: 300000,
      priority: 3
    },
    qualityTrends: {
      title: 'Quality Trends',
      description: 'Quality metrics trends over time',
      refreshInterval: 300000,
      priority: 4
    },
    recentActivity: {
      title: 'Recent Activity',
      description: 'Latest system activities',
      refreshInterval: 60000, // 1 minute
      priority: 5
    },
    actionItems: {
      title: 'Action Items',
      description: 'Outstanding action items',
      refreshInterval: 300000,
      priority: 6
    },
    capaStatusOverview: {
      title: 'CAPA Status',
      description: 'CAPA management overview',
      refreshInterval: 300000,
      priority: 7
    },
    trainingProgress: {
      title: 'Training Progress',
      description: 'Training completion tracking',
      refreshInterval: 300000,
      priority: 8
    },
    supplierOverview: {
      title: 'Supplier Overview',
      description: 'Supplier performance metrics',
      refreshInterval: 300000,
      priority: 9
    },
    complaintOverview: {
      title: 'Complaint Overview',
      description: 'Customer complaint tracking',
      refreshInterval: 300000,
      priority: 10
    },
    ncrOverview: {
      title: 'NCR Overview',
      description: 'Non-conformity report tracking',
      refreshInterval: 300000,
      priority: 11
    },
    todoManagement: {
      title: 'Todo Management',
      description: 'Task management overview',
      refreshInterval: 300000,
      priority: 12
    },
    riskMatrix: {
      title: 'Risk Matrix',
      description: 'Risk assessment visualization',
      refreshInterval: 300000,
      priority: 13
    }
  },

  // Dashboard layout configuration
  layout: {
    gridCols: {
      default: 'grid-cols-1',
      lg: 'lg:grid-cols-2',
      xl: 'xl:grid-cols-3'
    },
    spacing: 'gap-6',
    padding: 'p-6'
  },

  // Auto-refresh configuration
  autoRefresh: {
    enabled: true,
    interval: 300000, // 5 minutes
    widgets: {
      recentActivity: 60000, // 1 minute for more frequent updates
      default: 300000 // 5 minutes for most widgets
    }
  }
};

export default dashboardConfig;
